import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import OfflineIndicator from "./components/OfflineIndicator";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import VendingPage from "./pages/VendingPage";
import ProductsPage from "./pages/ProductsPage";
import OrdersPage from "./pages/OrdersPage";
import SuppliersPage from "./pages/SuppliersPage";
import RefillPage from "./pages/RefillPage";
import InventoryPage from "./pages/InventoryPage";
import CashPage from "./pages/CashPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import WishPage from "./pages/WishPage";
import PublicWishesPage from "./pages/PublicWishesPage";
import ProblemPage from "./pages/ProblemPage";
import ImportPage from "./pages/ImportPage";
import SupplierCatalogPage from "./pages/SupplierCatalogPage";
import UsersPage from "./pages/UsersPage";
import DiscountOffersPage from "./pages/DiscountOffersPage";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://snackomat.luenefire.de';
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("snack_auth") === "true";
  });
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("snack_user");
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem("snack_token") || null;
  });

  // Set up axios interceptor for auth token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = async (password, email = null) => {
    try {
      const payload = { password };
      if (email) payload.email = email;
      
      const res = await axios.post(`${API}/auth/login`, payload);
      
      if (res.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem("snack_auth", "true");
        
        if (res.data.token) {
          setToken(res.data.token);
          localStorage.setItem("snack_token", res.data.token);
        }
        
        if (res.data.user) {
          const userData = {
            ...res.data.user,
            must_change_password: res.data.must_change_password
          };
          setUser(userData);
          localStorage.setItem("snack_user", JSON.stringify(userData));
        }
        
        toast.success("Erfolgreich angemeldet");
        return true;
      }
      return false;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
      return false;
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem("snack_auth");
    localStorage.removeItem("snack_user");
    localStorage.removeItem("snack_token");
    delete axios.defaults.headers.common['Authorization'];
    toast.info("Abgemeldet");
  };

  const updateUser = (newUserData) => {
    setUser(newUserData);
    localStorage.setItem("snack_user", JSON.stringify(newUserData));
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout, updateUser, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <OfflineIndicator />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/wunsch" element={<WishPage />} />
          <Route path="/wuensche" element={<PublicWishesPage />} />
          <Route path="/problem" element={<ProblemPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="automat" element={<VendingPage />} />
            <Route path="produkte" element={<ProductsPage />} />
            <Route path="bestellungen" element={<OrdersPage />} />
            <Route path="lieferanten" element={<SuppliersPage />} />
            <Route path="auffuellen" element={<RefillPage />} />
            <Route path="lager" element={<InventoryPage />} />
            <Route path="kasse" element={<CashPage />} />
            <Route path="berichte" element={<ReportsPage />} />
            <Route path="angebote" element={<DiscountOffersPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="lieferanten-katalog" element={<SupplierCatalogPage />} />
            <Route path="einstellungen" element={<SettingsPage />} />
            <Route path="benutzer" element={<AdminRoute><UsersPage /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
